import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const CATEGORIES = [
  { value: 'enqueteurs_cati', label: 'Enquêteurs CATI' },
  { value: 'enqueteurs_f2f', label: 'Prestataires F2F' },
  { value: 'controle', label: 'Contrôle' },
  { value: 'deplacement', label: 'Déplacement' },
  { value: 'imprevus', label: 'Imprévus' },
  { value: 'autre', label: 'Autre' },
];

const emptyLine = () => ({ category: 'enqueteurs_cati', label: '', qty: '', unit_price: '', notes: '' });

const inputStyle = {
  width: '100%', fontSize: '0.78rem', padding: '6px 10px',
  border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none',
  background: '#fff', color: '#111827', transition: 'border-color 0.15s',
};

const BudgetEditor = ({ budget, slot, onSave, onSubmit, saving, isAdmin }) => {
  const [lines, setLines] = useState([emptyLine()]);

  useEffect(() => {
    if (budget && budget.lines && budget.lines.length > 0) {
      // Load existing budget
      setLines(budget.lines.map(l => ({
        category: l.category, label: l.label,
        qty: l.qty, unit_price: l.unit_price, notes: l.notes || '',
      })));
    } else if ((slot?.linked_surveys?.length || 0) > 0 || slot?.survey) {
      // No existing budget — auto-generate lines from what the slot covers.
      //
      // A slot may cover several surveys, so each linked (survey, vague) gets its own set
      // of lines rather than one blended set: unit prices differ between surveys and a
      // single averaged price would be wrong. Each line is labelled with its survey so the
      // breakdown stays readable. Vague-scoped links prefill from THAT vague's
      // target / marge / frais rather than the survey-wide roll-up.
      const links = (slot.linked_surveys?.length || 0) > 0
        ? slot.linked_surveys
        : [{ id: 'primary', survey: slot.survey, survey_wave: slot.survey_wave || null }];

      const multi = links.length > 1;
      const newLines = [];

      links.forEach((link, groupIndex) => {
        const survey = link.survey;
        if (!survey) return;
        const wave = link.survey_wave || null;
        const targetVal = wave ? wave.target : survey.general_target;
        const margeVal = wave ? wave.marge_imprevus : survey.marge_imprevus;
        const fraisVal = wave ? wave.frais_deplacement : survey.frais_deplacement;
        const isF2F = survey.poste_qcode === 'ref';
        // Only tag lines when there is more than one survey — keeps the single-survey
        // budget identical to what it produced before.
        const tag = multi
          ? `${survey.title}${wave ? ` — ${wave.label}` : ''}`
          : '';

        // _group shades each survey's block differently; it is display-only and is
        // dropped by buildPayload().
        const g = groupIndex;

        if (isF2F) {
          newLines.push({
            _group: g,
            category: 'enqueteurs_f2f', label: tag,
            qty: targetVal ? String(targetVal) : '',
            unit_price: survey.price_per_complete ? String(survey.price_per_complete) : '',
            notes: '',
          });
          newLines.push({ _group: g, category: 'controle', label: tag, qty: '', unit_price: '', notes: '' });
          newLines.push({
            _group: g,
            category: 'deplacement', label: tag,
            qty: '', unit_price: fraisVal ? String(fraisVal) : '',
            notes: '',
          });
          newLines.push({
            _group: g,
            category: 'imprevus', label: tag,
            qty: '', unit_price: margeVal ? String(margeVal) : '',
            notes: '',
          });
        } else {
          // CATI
          const isPrDay = survey.payment_type === 'per_day';
          newLines.push({
            _group: g,
            category: 'enqueteurs_cati', label: tag,
            qty: targetVal ? String(targetVal) : '',
            unit_price: isPrDay
              ? (survey.price_per_day ? String(survey.price_per_day) : '')
              : (survey.price_per_complete ? String(survey.price_per_complete) : ''),
            notes: '',
          });
          newLines.push({ _group: g, category: 'controle', label: tag, qty: '', unit_price: '', notes: '' });
          newLines.push({
            _group: g,
            category: 'imprevus', label: tag,
            qty: '', unit_price: margeVal ? String(margeVal) : '',
            notes: '',
          });
        }
      });

      setLines(newLines.length > 0 ? newLines : [emptyLine()]);
    } else {
      setLines([emptyLine()]);
    }
  }, [budget, slot]);

  const updateLine = (i, field, value) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (i) => { if (lines.length > 1) setLines(prev => prev.filter((_, idx) => idx !== i)); };

  const lineTotal = (l) => ((parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0)).toFixed(2);
  const totalCost = lines.reduce((s, l) => s + parseFloat(lineTotal(l)), 0);

  const isValid = lines.length > 0 && lines.every(l => {
    const isLumpSum = l.category === 'deplacement' || l.category === 'imprevus' || l.category === 'autre';
    if (isLumpSum) return parseFloat(l.unit_price) > 0;
    return (parseFloat(l.qty) || 0) > 0 && (parseFloat(l.unit_price) || 0) > 0;
  });

  const buildPayload = () => ({
    lines: lines.map(l => {
      const isLumpSum = l.category === 'deplacement' || l.category === 'imprevus' || l.category === 'autre';
      return {
        category: l.category, label: l.label,
        qty: isLumpSum ? 1 : (parseFloat(l.qty) || 0),
        unit_price: parseFloat(l.unit_price) || 0,
        notes: l.notes || null,
      };
    }),
  });

  const focusHandler = (e) => { e.target.style.borderColor = '#dc2626'; };
  const blurHandler = (e) => { e.target.style.borderColor = '#e5e7eb'; };

  return (
    <div>
      {/* Lines */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
          Lignes budgétaires
        </div>

        {lines.map((line, i) => {
          // Alternate the shading per survey block (2nd, 4th, ...) so several sets of
          // lines on one slot read as distinct groups.
          const shaded = (line._group ?? 0) % 2 === 1;
          // Lines from a saved budget, or added by hand, carry no _group — treat them as
          // group 0 so they render exactly as before, with no shading or divider.
          const startsGroup = i > 0 && (line._group ?? 0) !== (lines[i - 1]._group ?? 0);
          return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '150px 1fr 80px 100px 90px 40px',
            gap: 8, alignItems: 'center', padding: '8px 6px',
            borderBottom: '1px solid #f3f4f6',
            borderTop: startsGroup ? '1px solid #e5e7eb' : 'none',
            background: shaded ? '#fafafa' : 'transparent',
          }}>
            <select value={line.category} onChange={e => updateLine(i, 'category', e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', fontSize: '0.72rem' }}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>

            <input type="text" value={line.label} onChange={e => updateLine(i, 'label', e.target.value)}
              placeholder="Description" style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} />

            {(line.category === 'deplacement' || line.category === 'imprevus' || line.category === 'autre') ? (
              <>
                <div />
                <div />
                <input type="number" value={line.unit_price}
                  onChange={e => updateLine(i, 'unit_price', e.target.value)}
                  placeholder="Montant estimé" min="0" step="0.01"
                  style={{ ...inputStyle, textAlign: 'right', fontWeight: 700 }}
                  onFocus={focusHandler} onBlur={blurHandler} />
              </>
            ) : (
              <>
                <input type="number" value={line.qty} onChange={e => updateLine(i, 'qty', e.target.value)}
                  placeholder={line.category === 'controle' ? 'Nb jours' : 'Qté'}
                  min="0" step={line.category === 'controle' ? '1' : '0.01'}
                  style={{ ...inputStyle, textAlign: 'right' }} onFocus={focusHandler} onBlur={blurHandler} />
                <input type="number" value={line.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)}
                  placeholder={line.category === 'controle' ? 'Prix/jour' : 'Prix unit.'}
                  min="0" step="0.01"
                  style={{ ...inputStyle, textAlign: 'right' }} onFocus={focusHandler} onBlur={blurHandler} />
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827', textAlign: 'right', paddingRight: 4 }}>
                  {Number(lineTotal(line)).toLocaleString('fr-FR')}
                </div>
              </>
            )}

            <button onClick={() => removeLine(i)} disabled={lines.length <= 1}
              style={{ background: 'none', border: 'none', cursor: lines.length > 1 ? 'pointer' : 'default',
                color: lines.length > 1 ? '#d1d5db' : '#f3f4f6', padding: 4, transition: 'color 0.15s' }}
              onMouseEnter={e => { if (lines.length > 1) e.target.style.color = '#ef4444'; }}
              onMouseLeave={e => { e.target.style.color = lines.length > 1 ? '#d1d5db' : '#f3f4f6'; }}>
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </button>
          </div>
          );
        })}

        <div style={{
          display: 'grid', gridTemplateColumns: '150px 1fr 80px 100px 90px 40px',
          gap: 8, padding: '4px 0 0',
        }}>
          <div style={legendStyle}>Catégorie</div>
          <div style={legendStyle}>Libellé</div>
          <div style={{ ...legendStyle, textAlign: 'right' }}>Qté / Jours</div>
          <div style={{ ...legendStyle, textAlign: 'right' }}>Prix unitaire</div>
          <div style={{ ...legendStyle, textAlign: 'right' }}>Total</div>
          <div />
        </div>
      </div>

      <button onClick={addLine}
        style={{ fontSize: '0.72rem', fontWeight: 600, padding: '5px 12px', borderRadius: 6,
          border: '1px dashed #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer', marginBottom: 20 }}
        onMouseEnter={e => { e.target.style.borderColor = '#dc2626'; e.target.style.color = '#dc2626'; }}
        onMouseLeave={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.color = '#6b7280'; }}>
        <FontAwesomeIcon icon={faPlus} size="xs" className="me-1" /> Ajouter une ligne
      </button>

      {/* Summary */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
        padding: '12px 0', borderTop: '2px solid #f3f4f6', marginBottom: 16,
      }}>
        <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Coût total:</span>
        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
          {totalCost.toLocaleString('fr-FR')} MAD
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {!isAdmin && (
          <button onClick={() => onSave(buildPayload())} disabled={saving || !isValid}
            style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151',
              fontSize: '0.78rem', fontWeight: 500, cursor: (saving || !isValid) ? 'default' : 'pointer', opacity: (saving || !isValid) ? 0.5 : 1 }}>
            <FontAwesomeIcon icon={faSave} size="sm" className="me-1" />
            {saving ? 'Enregistrement...' : 'Enregistrer brouillon'}
          </button>
        )}
        <button onClick={() => isAdmin ? onSave(buildPayload()) : onSubmit(buildPayload())} disabled={saving || !isValid}
          style={{ padding: '7px 16px', borderRadius: 6, border: 'none',
            background: (saving || !isValid) ? '#e5e7eb' : '#dc2626', color: (saving || !isValid) ? '#9ca3af' : '#fff',
            fontSize: '0.78rem', fontWeight: 600, cursor: (saving || !isValid) ? 'default' : 'pointer' }}>
          <FontAwesomeIcon icon={isAdmin ? faSave : faPaperPlane} size="sm" className="me-1" />
          {saving ? 'Enregistrement...' : isAdmin ? 'Valider et enregistrer' : 'Soumettre'}
        </button>
      </div>
    </div>
  );
};

const legendStyle = {
  fontSize: '0.58rem', fontWeight: 600, color: '#9ca3af',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

export default BudgetEditor;
